'use client'

import { memo } from 'react'
import dynamic from 'next/dynamic'
import { useQuery } from '@tanstack/react-query'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

const Lottie = dynamic(() => import('lottie-react'), { ssr: false })

export type MarketingLottieProps = {
  /** Public path under /jsons: product assets only, never Webflow/Ninjapay */
  src: string
  label?: string
  className?: string
  /** Pause when the parent card is collapsed */
  active?: boolean
}

async function fetchLottieJson(src: string): Promise<unknown> {
  const response = await fetch(src)
  if (!response.ok) {
    throw new Error(`Failed to load Lottie: ${src}`)
  }
  return response.json()
}

/**
 * baby: Shared Lottie slot for marketing expand cards: loads via React Query,
 * skips animation when prefers-reduced-motion.
 */
export const MarketingLottie = memo(function MarketingLottie({
  src,
  label = 'Animation',
  className,
  active = true,
}: MarketingLottieProps) {
  const reduced = Boolean(useReducedMotion())
  const { data, isError } = useQuery({
    queryKey: ['marketing-lottie', src],
    queryFn: () => fetchLottieJson(src),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60 * 60,
  })

  if (isError || !data) {
    return (
      <div
        className={cn(
          'marketing-card-media-fallback flex h-24 w-full items-center justify-center',
          className,
        )}
        aria-hidden
      >
        <span className="ninxl-8bit-label tracking-[0.16em] text-[color:var(--ninxl-brand-teal)]">
          MEDIA
        </span>
      </div>
    )
  }

  return (
    <div
      className={cn('marketing-card-media-lottie mx-auto h-24 w-24 md:h-28 md:w-28', className)}
      aria-label={label}
      role="img"
    >
      <Lottie
        animationData={data}
        loop={!reduced && active}
        autoplay={!reduced && active}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
})
